import type {
  BoundType,
  BoundsType,
  TripSegmentType,
} from '../common/types/transformations/models';
import type { OrderStatusProcessingTripsBoundsType } from '../pages/processing-order/models';
import type { SelectTripWithDataSelectedTripBoundsType } from '../pages/result/models';

const getTripSegments = (bound: BoundType): TripSegmentType[] =>
  bound.segments?.filter(
    (segment): segment is TripSegmentType => segment.__typename === 'TripSegment',
  );

const getCarrierCodeInTripSegment = (segment: TripSegmentType) => {
  const ticketingCarrierCode = segment?.ticketingCarrier?.code?.toLowerCase() ?? '';
  const marketingCarrierCode = segment?.marketingCarrier?.code?.toLowerCase() ?? '';

  return { ticketingCarrierCode, marketingCarrierCode };
};

export const getTripSegmentsFromBounds = (bounds: BoundsType = []) => {
  return bounds.flatMap((bound) => getTripSegments(bound));
};

export const hasRyanAirTicketingOrMarketingCarrier = (
  carrierCodes: string[] = [],
  bounds: BoundsType,
) => {
  const lowerCaseRyanAirCarrierCodes = carrierCodes.map((carrier) => carrier.toLowerCase());
  const tripSegments = getTripSegmentsFromBounds(bounds) || [];

  return tripSegments.some((segment) => {
    const carrierTripCode = getCarrierCodeInTripSegment(segment);

    return (
      lowerCaseRyanAirCarrierCodes.includes(carrierTripCode.ticketingCarrierCode) ||
      lowerCaseRyanAirCarrierCodes.includes(carrierTripCode.marketingCarrierCode)
    );
  });
};

export const getMarketingCarrierCodes = (tripSegments: TripSegmentType[]) => {
  const marketingCarrierCodes: string[] = [];

  tripSegments.forEach((segment) => {
    if (segment.marketingCarrier?.code) {
      marketingCarrierCodes.push(segment.marketingCarrier.code);
    }
  });

  const uniqueMarketingCarrierCodes = new Set(marketingCarrierCodes);
  return [...uniqueMarketingCarrierCodes];
};

const getOperatingCarrierCodes = (tripSegments: TripSegmentType[]) => {
  const operatingCarrierCodes: string[] = [];

  tripSegments.forEach((segment) => {
    if (segment.operatingCarrier?.code) {
      operatingCarrierCodes.push(segment.operatingCarrier.code);
    }
  });

  const uniqueOperatingCarrierCodes = new Set(operatingCarrierCodes);
  return [...uniqueOperatingCarrierCodes];
};

const getTicketingCarrierCodes = (tripSegments: TripSegmentType[]) => {
  const ticketingCarrierCodes: string[] = [];

  tripSegments.forEach((segment) => {
    if (segment.ticketingCarrier?.code) {
      ticketingCarrierCodes.push(segment.ticketingCarrier.code);
    }
  });

  const uniqueTicketingCarrierCodes = new Set(ticketingCarrierCodes);

  return [...uniqueTicketingCarrierCodes];
};

export const getAllKindsOfCarrierCodes = (
  allBounds:
    | BoundType[]
    | OrderStatusProcessingTripsBoundsType
    | SelectTripWithDataSelectedTripBoundsType,
) => {
  const allTripSegments = allBounds.flatMap(
    (bound) =>
      bound.segments?.filter(
        (segment): segment is TripSegmentType => segment.__typename === 'TripSegment',
      ),
  );

  return {
    marketingCarrierCodes: getMarketingCarrierCodes(allTripSegments).join(', '),
    operatingCarrierCodes: getOperatingCarrierCodes(allTripSegments).join(', '),
    ticketingCarrierCodes: getTicketingCarrierCodes(allTripSegments).join(', '),
  };
};
